import styled from '@emotion/styled'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useAppContent,
  useFulfillment,
  useImages,
  useShop,
  useShopPage
} from 'shop/hooks'
import { StyledHTMLElement } from 'shop/theme/types'
import { ImageTagType } from 'shop/types'
import { DESKTOP_QUERY, MOBILE_QUERY } from 'shop/utils/common'
import {
  DEFAULT_NAVBAR_HEIGHT,
  EXPANDED_NAVBAR_HEIGHT
} from '../NavBar/commonStyles'
import { fadeIn, iosGpuRenderFixProperties } from '../Shop/commonStyles'
import Fulfillment from '../Shop/Fulfillment/Fulfillment'

interface Props {
  backgroundImage?: string
  heading?: string
  subheading?: string
}

const Banner = ({ backgroundImage, heading }: Props) => {
  const { bannerImage } = useAppContent()
  const { currentPartnerStore, partner } = useShop()
  const { isShopSearchVisible } = useShopPage()
  const { isOat } = useFulfillment()

  const [isLoaded, setIsLoaded] = useState(false)
  const [isFulfillmentSticky, setIsFulfillmentSticky] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const fulfillmentWrapperRef = useRef<HTMLDivElement>(null)

  const { name: storeName = '' } = currentPartnerStore || {}

  const img = backgroundImage || bannerImage
  const alt =
    partner?.name && storeName ? `${partner?.name} ${storeName}` : heading

  const navbarHeight = isShopSearchVisible
    ? EXPANDED_NAVBAR_HEIGHT
    : DEFAULT_NAVBAR_HEIGHT

  const { getImage, handleImageError } = useImages(
    [
      { imageUrl: img, mediaQuery: DESKTOP_QUERY },
      { imageUrl: img, mediaQuery: MOBILE_QUERY }
    ],
    ImageTagType.Responsive
  )

  /** iOS Safari Fix: Prevents flickering during rapid position changes
   * Issue: When scrolling fast to top, fixed → absolute transition causes element to jump to top of page
   * Solution: Block new state changes during CSS transition (200ms) to prevent race conditions */
  const handleStickyChange = useCallback(
    (isIntersecting: boolean) => {
      const shouldBeSticky = !isIntersecting

      // Only trigger transition if state actually needs to change and we're not already transitioning
      if (!isTransitioning && shouldBeSticky !== isFulfillmentSticky) {
        setIsTransitioning(true)
        setIsFulfillmentSticky(shouldBeSticky)
        // Wait 250ms (longer than 200ms CSS transition) to prevent iOS Safari positioning race condition
        setTimeout(() => setIsTransitioning(false), 250)
      }
    },
    [isTransitioning, isFulfillmentSticky]
  )

  useEffect(() => {
    const intersectionOffset = navbarHeight * 1.9
    const observer = new IntersectionObserver(
      ([entry]) => {
        handleStickyChange(entry.isIntersecting)
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${intersectionOffset}px 0px 0px 0px`
      }
    )

    const fulfillmentComponent = fulfillmentWrapperRef.current
    if (fulfillmentComponent) {
      observer.observe(fulfillmentComponent)
    }

    return () => {
      if (fulfillmentComponent) {
        observer.unobserve(fulfillmentComponent)
      }
    }
  }, [navbarHeight, handleStickyChange])

  return (
    <Root>
      <ImageContainer>
        {img && (
          <picture>
            <source
              media={DESKTOP_QUERY}
              srcSet={getImage(1914, 386, DESKTOP_QUERY, `fit=cover`)}
            />
            <source
              media={MOBILE_QUERY}
              srcSet={getImage(478, 160, MOBILE_QUERY, `fit=cover`)}
            />
            <Background
              // fetchPriority not recognised by React 18 (Seems to be a bug: https://github.com/facebook/react/issues/28948)
              // Using suggeested workaround for now
              // TODO: Remove when we upgrade React versions
              {...{ fetchpriority: 'high' }}
              src={img}
              alt={alt}
              data-testid='banner-image'
              isLoaded={isLoaded}
              onLoad={() => setIsLoaded(true)}
              onError={handleImageError}
            />
          </picture>
        )}
        {!isOat && (
          <div ref={fulfillmentWrapperRef}>
            <FulfillmentContainer
              isSticky={isFulfillmentSticky}
              isShopSearchVisible={isShopSearchVisible}
            >
              <Fulfillment isStickyMobile={isFulfillmentSticky} />
            </FulfillmentContainer>
          </div>
        )}
      </ImageContainer>
    </Root>
  )
}

const Root = styled.div(({ theme }) => ({
  position: 'relative',
  transition: 'all 100ms ease-in-out',
  margin: '0 auto',
  maxWidth: '1920px',
  padding: '0 0 16px',
  [theme.mediaQueries.viewport7]: {
    maxWidth: '1332px',
    padding: '0 16px 16px'
  },
  [theme.mediaQueries.viewport14]: {
    maxWidth: '2064px',
    padding: '0 75px 16px'
  }
}))

const ImageContainer = styled.div(({ theme }) => ({
  aspectRatio: '3/1',
  height: 'auto',
  width: 'auto',
  [theme.mediaQueries.viewport7]: {
    aspectRatio: '5/1'
  }
}))

const FulfillmentContainer = styled.div<{
  isSticky: boolean
  isShopSearchVisible: boolean
}>(({ theme, isSticky, isShopSearchVisible }) => ({
  // Base styles
  border: '1px solid #D9D9D9',
  transition: 'all 0.2s ease-in-out',
  WebkitTransition: 'all 0.2s ease-out',
  // iOS Safari optimization - apply to both states for smooth transitions
  ...iosGpuRenderFixProperties,
  // Default (absolute) positioning
  position: 'absolute',
  width: '96%',
  left: '0',
  right: '0',
  marginLeft: '2%',
  marginRight: '2%',
  bottom: '24px',
  borderRadius: theme.borderRadius,
  // Sticky state overrides
  ...(isSticky && {
    position: 'fixed',
    width: '100%',
    marginLeft: '0',
    marginRight: '0',
    top: isShopSearchVisible ? EXPANDED_NAVBAR_HEIGHT : DEFAULT_NAVBAR_HEIGHT,
    bottom: 'auto',
    zIndex: 11,
    borderRadius: '0'
  }),
  [theme.mediaQueries.viewport4]: {
    right: '12px',
    width: 'max-content',
    left: 'auto',
    transform: 'none',
    marginLeft: '0',
    marginRight: '0',
    ...(isSticky && {
      right: 'auto',
      width: '100%',
      left: '0'
    })
  },
  [theme.mediaQueries.viewport7]: {
    right: '24px',
    position: 'absolute',
    width: 'max-content',
    left: 'auto',
    transform: 'none',
    bottom: '24px',
    top: 'auto',
    zIndex: 'auto',
    borderRadius: theme.borderRadius
  },
  [theme.mediaQueries.viewport9]: {
    display: 'none'
  }
}))

const Background = styled.img<StyledHTMLElement & { isLoaded: boolean }>(
  ({ isLoaded, theme }) => ({
    height: '100%',
    width: '100%',
    objectFit: 'cover',
    display: 'block',
    [theme.mediaQueries.viewport7]: {
      borderRadius: '0 0 8px 8px'
    },
    opacity: 0,
    animation: isLoaded ? `${fadeIn} 0.5s ease-in-out forwards` : 'none'
  })
)

export default Banner
